import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Radar, TrendingUp, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("comercial");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register({ email, password, name, role });
      navigate("/dashboard");
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="flex min-h-screen bg-slate-950 text-white">
      {/* Left brand panel */}
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden p-12 lg:flex">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-sky-950" />
        <div className="absolute -right-24 top-1/3 h-96 w-96 rounded-full bg-sky-600/20 blur-3xl" />
        <div className="absolute -left-16 bottom-0 h-80 w-80 rounded-full bg-emerald-600/20 blur-3xl" />
        <div className="relative flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-xl bg-white/10 backdrop-blur">
            <ShieldCheck className="h-6 w-6 text-emerald-400" />
          </div>
          <div>
            <div className="font-display text-xl font-extrabold tracking-tight">PRO INVICTA HUB</div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-sky-400">Centro de Comando</div>
          </div>
        </div>
        <div className="relative space-y-6">
          <h1 className="font-display text-4xl font-bold leading-tight">
            Radar de oportunidades públicas + gestão completa de licitações.
          </h1>
          <p className="max-w-md text-slate-300">
            Encontre, analise e vença licitações no PNCP. Da oportunidade ao financeiro, tudo em um só lugar.
          </p>
          <div className="grid grid-cols-3 gap-4 text-sm">
            {[
              { i: Radar, t: "Radar PNCP" },
              { i: TrendingUp, t: "Score de aderência" },
              { i: ShieldCheck, t: "Gestão & DRE" },
            ].map((f, k) => (
              <div key={k} className="rounded-xl border border-white/10 bg-white/5 p-4">
                <f.i className="mb-2 h-5 w-5 text-emerald-400" />
                <div className="font-medium">{f.t}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative text-xs text-slate-500">PRO INVICTA COMERCIO ADS — {new Date().getFullYear()}</div>
      </div>

      {/* Right form */}
      <div className="flex w-full items-center justify-center bg-white p-6 text-slate-900 lg:w-1/2">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="mb-8 flex items-center gap-2 lg:hidden">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-slate-900">
              <ShieldCheck className="h-5 w-5 text-emerald-400" />
            </div>
            <span className="font-display text-lg font-extrabold">PRO INVICTA HUB</span>
          </div>
          <h2 className="font-display text-2xl font-bold">{mode === "login" ? "Entrar" : "Criar conta"}</h2>
          <p className="mt-1 text-sm text-slate-500">Acesse o centro de comando da sua empresa.</p>

          <div className="mt-6 flex rounded-lg bg-slate-100 p-1 text-sm font-medium">
            <button data-testid="tab-login" onClick={() => setMode("login")} className={`flex-1 rounded-md py-1.5 ${mode === "login" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>Entrar</button>
            <button data-testid="tab-register" onClick={() => setMode("register")} className={`flex-1 rounded-md py-1.5 ${mode === "register" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>Criar conta</button>
          </div>

          <form onSubmit={submit} className="mt-6 space-y-4">
            {mode === "register" && (
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">Nome</label>
                <input data-testid="register-name" value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-sky-400" placeholder="Seu nome" />
              </div>
            )}
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600">E-mail</label>
              <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-sky-400" placeholder="voce@empresa.com" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600">Senha</label>
              <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-sky-400" placeholder="••••••••" />
            </div>
            {mode === "register" && (
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">Perfil de acesso</label>
                <select data-testid="register-role" value={role} onChange={(e) => setRole(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-sky-400">
                  <option value="comercial">Comercial</option>
                  <option value="financeiro">Financeiro</option>
                  <option value="operacional">Operacional</option>
                  <option value="consulta">Consulta</option>
                  <option value="admin">Administrador</option>
                </select>
              </div>
            )}
            {err && <div data-testid="login-error" className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{err}</div>}
            <button data-testid="login-submit" disabled={loading} className="flex w-full items-center justify-center gap-2 rounded-lg bg-sky-600 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-sky-700 disabled:opacity-60">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {mode === "login" ? "Entrar" : "Criar conta"}
            </button>
          </form>

          <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
            <div className="h-px flex-1 bg-slate-200" /> ou <div className="h-px flex-1 bg-slate-200" />
          </div>
          <button data-testid="google-login-btn" onClick={googleLogin} className="flex w-full items-center justify-center gap-2 rounded-lg border border-slate-200 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5" />
            Continuar com Google
          </button>
        </div>
      </div>
    </div>
  );
}
